import styles from '@styles/scss/performer/_imageGallery.module.scss';
import { ImageGallery } from 'lib/types/item/imageGallery';
import { Performer } from 'lib/types/item/performer';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import ImageLightBox from './imageLightBox';
import { ImageGalleryCta } from './imageGalleryCta';
import { loadKraken } from 'lib/functions/loadKraken';

export default function PerformerGallery({
    performer,
    sendData,
    imageGallery,
    blurStatus,
    gender,
    currentLang
}: {
    performer: Performer;
    sendData: any;
    imageGallery: ImageGallery;
    blurStatus: string;
    gender: string;
    currentLang: string;
}): JSX.Element {
    const images = performer.gallery_images ? performer.gallery_images : [performer.gallery_image];

    const [currentSection, setCurrentSection] = useState(0);
    const [blur, setBlur] = useState('');
    const [width, setWidth] = useState(0);
    const [lightBox, setLightBox] = useState(false);
    const [lightboxImageIndex, setLightboxImageIndex] = useState(0);

    const isMobile = width <= 450;

    const ctaElement = <ImageGalleryCta key="cta" performer={performer} gender={gender} currentLang={currentLang} imageGallery={imageGallery}/>;
    // Allow both strings and React elements in the array
    let imagesWithCta: (string | JSX.Element)[] = [...images];

   const ctaInterval = 10;
    const ctaElementsToInsert = Math.floor(imagesWithCta.length / ctaInterval);

    for (let i = 1; i <= ctaElementsToInsert; i++) {
      // Insert CTA after every 10th image
      imagesWithCta.splice(i * ctaInterval + (i - 1), 0, ctaElement);
    }

    // If less than 10 images, add CTA at the end
    if (imagesWithCta.length < ctaInterval) {
      imagesWithCta.push(ctaElement);
    }

    const length = imagesWithCta.length;
    const imagesPerSection = 2;

    const totalSections = Math.ceil(imagesWithCta.length / imagesPerSection);

    const getLightBox = function (val: boolean) {
        setLightBox(val);
    };

    const getBlur = function (val: string) {
        sendData(val);
        setBlur('');
    };

    useEffect(() => {
        setWidth(window.innerWidth);
        const handleResizeWindow = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResizeWindow);
        return () => {
            window.removeEventListener('resize', handleResizeWindow);
        };
    }, []);

    const getImagesForCurrentSection = () => {
        const startIndex = currentSection * imagesPerSection;
        return imagesWithCta.slice(startIndex, startIndex + imagesPerSection);
    };

    const nextSlide = () => {
      loadKraken('image-gallery');
    if (lightBox) {
        setLightboxImageIndex((prev) => (prev < imagesWithCta.length - 1 ? prev + 1 : prev));
    } else {
        setCurrentSection((prev) => (prev < totalSections - 1 ? prev + 1 : prev));
    }
  };

    const prevSlide = () => {
        loadKraken('image-gallery');
        if (lightBox) {
            setLightboxImageIndex((prev) => (prev > 0 ? prev - 1 : prev));
        } else {
            setCurrentSection((prev) => (prev > 0 ? prev - 1 : prev));
        }
    };

    const firstImage = !lightBox ? currentSection === 0 : lightboxImageIndex === 0;
    const lastImage = !lightBox
    ? currentSection === totalSections - 1
    : lightboxImageIndex === imagesWithCta.length - 1;

    const lightBoxToggle = (index: number) => {
        /* After light box is toggled, uses index to determine which image to choose, adds blur */
        setLightBox(true);
        sendData('blur-gallery');
        setBlur('blur-gallery');
        const imageIndex = currentSection * imagesPerSection + index;
        setLightboxImageIndex(imageIndex);
    };

    function outSideArrows(ref2: any, ref3: any, lightBoxRef: any) {
        /* handles clicking anywhere outside of the chevrons or the lightbox to close it,
      and remove the blur */
        useEffect(() => {
            function handleClickOutside(event: any) {
                if (lightBox === true && getImagesForCurrentSection.length === 1) {
                    setLightBox(false);
                    sendData('');
                    setBlur('');
                } else if (
                    lightBox === true &&
                    ref2.current &&
                    !ref2.current.contains(event.target) &&
                    ref3.current &&
                    !ref3.current.contains(event.target) &&
                    lightBoxRef.current &&
                    !lightBoxRef.current.contains(event.target)
                ) {
                    setLightBox(false);
                    sendData('');
                    setBlur('');
                }
            }

            document.addEventListener('mousedown', handleClickOutside, { passive: true });
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [lightBox, ref2, ref3, lightBoxRef]);
    }

    const rightRef = useRef(null);
    const leftRef = useRef(null);
    const lightBoxRef = useRef(null);
    outSideArrows(rightRef, leftRef, lightBoxRef);


    return (
        <div className={styles.container}>
            <h2 className={blur != '' ? styles.blur : styles.noBlur}>
                {imageGallery.media_title}
            </h2>
            <div className={styles.slider}>
                {(lightBox && length >= 2) || length > 2 ? (
                    <div>
                        <svg
                            ref={leftRef}
                            className={`${styles.leftArrow} ${firstImage ? styles.disabled : ''} ${blurStatus === 'blur-video' ? styles.blurred : ''}`}
                            onClick={prevSlide}
                            width="30"
                            height="31"
                            viewBox="0 0 30 31"
                            fill="none"
                            xmlns="https://www.w3.org/2000/svg">
                            <rect y="0.5" width="30" height="30" rx="15" fill={firstImage ? "#919191" : "#222222"} />
                            <path
                                d="M17.8572 8.35717L10.7143 15.5L17.8572 22.6429"
                                stroke="white"
                                strokeWidth="1.42857"
                                strokeMiterlimit="10"
                                strokeLinecap="square"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <svg
                            ref={rightRef}
                            className={`${styles.rightArrow} ${lastImage ? styles.disabled : ''} ${blurStatus === 'blur-video' ? styles.blurred : ''}`}
                            onClick={nextSlide}
                            width="30"
                            height="31"
                            viewBox="0 0 30 31"
                            fill="none"
                            xmlns="https://www.w3.org/2000/svg">
                            <rect y="0.5" width="30" height="30" rx="15" fill={lastImage ? "#919191" : "#222222"} />
                            <path
                                d="M12.1428 8.35717L19.2857 15.5L12.1428 22.6429"
                                stroke="white"
                                strokeWidth="1.42857"
                                strokeMiterlimit="10"
                                strokeLinecap="square"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                ) : null}
                <div className={styles.sliderContainer}>
                  {getImagesForCurrentSection().map((image: string | JSX.Element, index: number) =>
                    React.isValidElement(image) ? (
                      <div className={styles.imgContainer} key="cta">
                        {image}
                      </div>
                    ) : (
                      typeof image === 'string' && (
                        <div
                          className={styles.imgContainer}
                          key={index}
                          role="presentation"
                          onClick={() => lightBoxToggle(index)}
                        >
                          <Image
                            loader={() => image}
                            src={image}
                            alt={`${performer.username} Photo ${index + 1} cam`}
                            height={isMobile ? 600 : 311}
                            width={isMobile ? 400 : 500} 
                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                          />
                        </div>
                      )
                    )
                  )}
                </div>
                <div ref={lightBoxRef} style={{ display: 'contents' }}>
                  <ImageLightBox
                    images={imagesWithCta}
                    lightBox={lightBox}
                    sendLightBox={getLightBox}
                    sendBlur={getBlur}
                    performer={performer}
                    currentIndex={lightboxImageIndex}
                    isMobile={isMobile}
                  />
                </div>
            </div>
        </div>
    );
}
